/**
 * Manual Link Search Dialog
 *
 * Searches F&O jobs with debounced text input and allows linking
 * a Role Master entry to a selected F&O Job.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Search, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import roleMasterService from '@/services/api/roleMasterService';

interface ManualLinkSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryId: number;
  entryTitle: string;
  onLinked: () => void;
}

const ManualLinkSearchDialog: React.FC<ManualLinkSearchDialogProps> = ({
  open,
  onOpenChange,
  entryId,
  entryTitle,
  onLinked,
}) => {
  const [foJobSearch, setFoJobSearch] = useState(() => {
    // Default search to cleaned entry title (strip trailing numbers and parentheticals)
    return entryTitle.replace(/\s+\d+$/, '').replace(/\s*\([^)]*\)/g, '').trim();
  });
  const [foJobResults, setFoJobResults] = useState<
    Array<{ job_id: string; title_id: string; description: string; job_family_id: string; function_id: string }>
  >([]);
  const [isSearchingJobs, setIsSearchingJobs] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialSearchDone = useRef(false);

  const handleSearchFoJobs = useCallback(async (query: string) => {
    if (query.length < 2) {
      setFoJobResults([]);
      return;
    }
    setIsSearchingJobs(true);
    try {
      const data = await roleMasterService.searchFoJobs(query);
      setFoJobResults(data.jobs || []);
    } catch (error) {
      console.error('F&O job search failed:', error);
      setFoJobResults([]);
    } finally {
      setIsSearchingJobs(false);
    }
  }, []);

  // Run initial search on mount
  React.useEffect(() => {
    if (open && !initialSearchDone.current && foJobSearch.length >= 2) {
      initialSearchDone.current = true;
      handleSearchFoJobs(foJobSearch);
    }
  }, [open, foJobSearch, handleSearchFoJobs]);

  const handleSearchChange = (value: string) => {
    setFoJobSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => handleSearchFoJobs(value), 400);
  };

  const handleManualLink = async (job: { job_id: string; title_id: string }) => {
    try {
      await roleMasterService.linkJob(entryId, job.job_id, job.title_id, false);
      onOpenChange(false);
      onLinked();
    } catch (error) {
      console.error('Manual link failed:', error);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-lg">Manual Link: Search F&O Jobs</h3>
          <p className="text-sm text-gray-500 mt-1">
            Linking <span className="font-medium text-gray-900">"{entryTitle}"</span>
          </p>
        </div>
        <div className="p-4 border-b">
          <Input
            placeholder="Search F&O Jobs by title..."
            value={foJobSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '400px' }}>
          {isSearchingJobs && (
            <p className="text-center text-gray-400 py-4">Searching...</p>
          )}
          {!isSearchingJobs && foJobResults.length === 0 && foJobSearch.length >= 2 && (
            <p className="text-center text-gray-400 py-4">No F&O Jobs found for "{foJobSearch}"</p>
          )}
          {foJobResults.map((job) => (
            <div
              key={job.job_id}
              className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer border-b last:border-0"
              onClick={() => handleManualLink(job)}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{job.title_id || job.description || job.job_id}</p>
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-mono text-gray-400">{job.job_id}</span>
                  {job.job_family_id && <span className="ml-2">• Family: {job.job_family_id}</span>}
                  {job.function_id && <span className="ml-2">• Function: {job.function_id}</span>}
                </p>
              </div>
              <Button size="sm" variant="outline" className="ml-3 flex-shrink-0 text-xs">
                <Link2 className="h-3 w-3 mr-1" /> Link
              </Button>
            </div>
          ))}
        </div>
        <div className="p-3 border-t flex justify-end">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualLinkSearchDialog;
